import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import { cn } from '@/shared/lib/utils';
import { LanguageSwitcher } from './LanguageSwitcher';

interface PreAuthShellProps {
  variant?: 'split' | 'single';
  brandSlot?: ReactNode;
  children: ReactNode;
  showBackToSite?: boolean;
  showFoundersLink?: boolean;
}

/**
 * Pre-auth chrome wrapping Login / Signup / Forgot / Reset / Onboarding.
 * Per docs/design/auth.md § Pre-auth shell — no sidebar, no status bar.
 */
export function PreAuthShell({
  variant = 'single',
  brandSlot,
  children,
  showBackToSite = true,
  showFoundersLink = false,
}: PreAuthShellProps) {
  const { t } = useTranslation('onboarding');

  return (
    <div className="relative flex min-h-screen flex-col bg-background text-foreground">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 overflow-hidden"
        style={{
          backgroundImage:
            'radial-gradient(ellipse 70% 50% at 50% 20%, rgba(245,166,35,0.10), transparent 70%)',
        }}
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-40"
        style={{
          backgroundImage:
            'linear-gradient(rgba(255,255,255,0.025) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.025) 1px, transparent 1px)',
          backgroundSize: '48px 48px',
          maskImage: 'radial-gradient(ellipse 60% 50% at 50% 30%, #000 35%, transparent 85%)',
          WebkitMaskImage: 'radial-gradient(ellipse 60% 50% at 50% 30%, #000 35%, transparent 85%)',
        }}
      />

      <header className="relative z-10 flex items-center justify-between border-b border-border/60 px-6 py-4 sm:px-10 sm:py-5">
        <div className="flex items-center gap-3">
          <div
            className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg border border-amber-500/25 bg-amber-500/10"
            aria-hidden
          >
            <img src="/loki-icon.png" alt="" className="h-full w-full object-contain" />
          </div>
          <span className="font-semibold tracking-tight">
            Loki <span className="text-amber-500">Trader</span>
          </span>
        </div>
        <div className="flex items-center gap-3">
          {showBackToSite && (
            <Link
              to="/"
              className="hidden items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground sm:inline-flex"
            >
              <ArrowLeft className="h-3 w-3" />
              {t('shell.backToSite')}
            </Link>
          )}
          <LanguageSwitcher />
        </div>
      </header>

      <main
        className={cn(
          'relative z-10 flex flex-1 flex-col',
          variant === 'split' ? 'lg:grid lg:grid-cols-2' : '',
        )}
      >
        {variant === 'split' && brandSlot && (
          <aside className="hidden flex-col justify-between border-r border-border/60 px-10 py-12 lg:flex">
            {brandSlot}
          </aside>
        )}
        <section className="flex flex-1 flex-col items-center justify-center px-6 py-10 sm:px-10">
          <div className="w-full max-w-md">{children}</div>
        </section>
      </main>

      <footer className="relative z-10 flex flex-wrap items-center justify-center gap-3 border-t border-border/60 px-6 py-4 font-mono text-[10.5px] uppercase tracking-wider text-muted-foreground/70">
        <span>© 2026 Loki Trader</span>
        <span className="text-muted-foreground/40">·</span>
        <Link to="/help" className="hover:text-foreground">
          {t('shell.help')}
        </Link>
        <span className="text-muted-foreground/40">·</span>
        <Link to="/docs" className="hover:text-foreground">
          {t('shell.docs')}
        </Link>
        <span className="text-muted-foreground/40">·</span>
        <Link to="/status" className="hover:text-foreground">
          {t('shell.status')}
        </Link>
        {showFoundersLink && (
          <>
            <span className="text-muted-foreground/40">·</span>
            <a
              href="mailto:founders@loki-trader.com"
              className="text-amber-500 hover:text-amber-400"
            >
              {t('shell.talkToFounders')}
            </a>
          </>
        )}
      </footer>
    </div>
  );
}
